<template>
  <main
    :class="[
      'mx-auto',
      'xl:max-w-6xl',
      'lg:max-w-4xl',
      'px-0',
      'sm:px-8',
      'lg:px-10',
      'mt-4',
      'mb-16',
      'sm:mt-0'
    ]"
  >
    <FormSection
      title="Customer lookup"
      subtitle="View and modify customer information. For infinite values, enter 0 (zero)."
    >
      <div v-if="!customer">
        <TextInput
          id="customer_email_address"
          :label="'Email address'"
          v-model="customerEmail"
          class="col-span-2"
          @keydown.enter="lookupCustomer"
        />
      </div>
      <div v-else class="space-y-6">
        <div>
          <legend class="text-base font-medium">
            {{ customer.displayName || "Unknown" }}
          </legend>
          <p class="text-sm text-white text-opacity-75">
            {{ customer.email || "n/a" }}
          </p>
        </div>
        <div class="space-y-6">
          <div>
            <label for="uid_copy" class="block text-sm font-medium">
              Copy UID
            </label>
            <button
              name="uid_copy"
              id="uid_copy"
              @click="copy(customer.uid)"
              class="mt-1 w-full flex-none bg-gray-700 text-gray-400 hover:text-gray-300 font-mono leading-6 py-1 px-2 border border-gray-500 rounded flex items-center justify-between space-x-2 sm:space-x-4 focus:outline-none transition-colors duration-200"
            >
              <span class="text-gray-300 hover:text-gray-100">
                {{ customer.uid }}
              </span>
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path
                  d="M8 16c0 1.886 0 2.828.586 3.414C9.172 20 10.114 20 12 20h4c1.886 0 2.828 0 3.414-.586C20 18.828 20 17.886 20 16v-4c0-1.886 0-2.828-.586-3.414C18.828 8 17.886 8 16 8m-8 8h4c1.886 0 2.828 0 3.414-.586C16 14.828 16 13.886 16 12V8m-8 8c-1.886 0-2.828 0-3.414-.586C4 14.828 4 13.886 4 12V8c0-1.886 0-2.828.586-3.414C5.172 4 6.114 4 8 4h4c1.886 0 2.828 0 3.414.586C16 5.172 16 6.114 16 8"
                ></path>
              </svg>
            </button>
          </div>
          <div>
            <label for="open_in_fb" class="block text-sm font-medium">
              View in Firestore
            </label>
            <a
              :href="
                `${fbURL}/firestore/data/users/${customer.uid}/product_data/colab`
              "
              target="_blank"
              name="open_in_fb"
              id="open_in_fb"
              class="mt-1 w-full flex-none bg-gray-700 text-gray-400 hover:text-gray-300 font-mono leading-6 py-1 px-2 border border-gray-500 rounded flex items-center justify-between space-x-2 sm:space-x-4 focus:outline-none transition-colors duration-200 overflow-hidden"
            >
              <span class="text-gray-300 hover:text-gray-100">{{
                `/users/${customer.uid}/product_data/colab`
              }}</span>
              <svg style="width:24px;height:24px" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z"
                />
              </svg>
            </a>
          </div>
        </div>
        <hr class="border-gray-600" />
        <div class="grid grid-cols-2 gap-y-6 gap-x-4">
          <TextInput
            class="col-span-2"
            label="Storage quota (GB)"
            v-model.number="customerQuotas.storage"
            @keydown.enter="saveCustomerData"
          />
          <TextInput
            label="Project quota"
            v-model.number="customerQuotas.projects"
            @keydown.enter="saveCustomerData"
          />
          <TextInput
            label="Collaborator quota"
            v-model.number="customerQuotas.projectCollaborators"
            @keydown.enter="saveCustomerData"
          />
        </div>
        <hr class="border-gray-600" />
        <div>
          <label for="project_list" class="block text-sm font-medium">
            Projects ({{ numberOfProjects }})
          </label>
          <ul class="mt-1 list-disc list-inside space-y-1">
            <li
              class="text-sm font-light"
              v-for="(project, projectid) in customerProjects"
              :key="projectid"
            >
              <a
                class="underline text-samplyBlue hover:text-samplyCyan transition-colors"
                :href="`${fbURL}/firestore/data/projects/${projectid}`"
                target="_blank"
              >
                {{ project.name }}
              </a>
            </li>
          </ul>
        </div>
        <div>
          <label for="project_list" class="block text-sm font-medium">
            Shared Projects ({{ numberOfSharedProjects }})
          </label>
          <ul class="mt-1 list-disc list-inside space-y-1">
            <li
              class="text-sm font-light"
              v-for="(project, projectid) in customerSharedProjects"
              :key="projectid"
            >
              <a
                class="underline text-samplyBlue hover:text-samplyCyan transition-colors"
                :href="`${fbURL}/firestore/data/projects/${projectid}`"
                target="_blank"
              >
                {{ project.name }}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <template v-slot:actions>
        <div v-if="customerLoading">
          <div class="py-2 px-4 text-sm font-medium border border-transparent">
            Loading...
          </div>
        </div>
        <div v-else-if="customer">
          <Button @click="customer = null">
            Restart
          </Button>
          <Button class="ml-2" @click="saveCustomerData">
            Save
          </Button>
        </div>
        <div v-else>
          <Button @click="lookupCustomer">
            Search
          </Button>
        </div>
      </template>
    </FormSection>
  </main>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import firebase from "firebase/app";
import Button from "@/components/Button.vue";
import TextInput from "@/components/TextInput.vue";
import FormSection from "./FormSection.vue";

let customerDataUnsub: Function;

const ONE_GB = 1000000000;
const FIREBASE_URL =
  process.env.VUE_APP_ENDPOINTS === "production"
    ? "https://console.firebase.google.com/u/0/project/samply-a03ff"
    : "https://console.firebase.google.com/u/0/project/samply-dev";

export default defineComponent({
  components: {
    Button,
    FormSection,
    TextInput
  },
  data() {
    return {
      /** True after the onAuthStateChanged has fired for the first time */
      authStateKnown: false,
      /** User is a Samply administrator */
      isAdmin: false,
      /** User object for the admin */
      admin: null as null | firebase.User,
      /** Email of the customer being searched for */
      customer: null as null | firebase.User,
      customerEmail: "",
      customerLoading: false,
      customerQuotas: {
        /** Storage quota converted to Gigabytes */
        storage: 0,
        /** Number of projects alloted to the customer */
        projects: 0,
        /** Number of collaborators  */
        projectCollaborators: 0
      },
      customerProjects: undefined,
      customerSharedProjects: undefined,
      fbURL: FIREBASE_URL
    };
  },
  computed: {
    numberOfProjects(): number {
      return Object.keys(this.customerProjects || {}).length;
    },
    numberOfSharedProjects(): number {
      return Object.keys(this.customerSharedProjects || {}).length;
    }
  },
  methods: {
    async copy(text: string) {
      await navigator.clipboard.writeText(text);
    },
    async saveCustomerData() {
      this.customerLoading = true;
      await firebase
        .firestore()
        .collection("users")
        .doc(this.customer?.uid)
        .collection("product_data")
        .doc("colab")
        .update({
          storageQuota: this.customerQuotas.storage * ONE_GB ?? null,
          projectQuota: this.customerQuotas.projects ?? null,
          projectCollaboratorQuota:
            this.customerQuotas.projectCollaborators ?? null
        });
      this.customerLoading = false;
    },
    async fetchCustomerData() {
      const { uid } = this.customer || {};
      const userRef = firebase
        .firestore()
        .collection("users")
        .doc(uid)
        .collection("product_data")
        .doc("colab");
      if (customerDataUnsub) customerDataUnsub();
      customerDataUnsub = userRef.onSnapshot(snap => {
        const productData = snap.data();
        // Extract data
        this.customerQuotas.storage = productData?.storageQuota / ONE_GB ?? 0;
        this.customerQuotas.projects = productData?.projectQuota ?? 0;
        this.customerQuotas.projectCollaborators =
          productData?.projectCollaboratorQuota ?? 0;
        this.customerProjects = productData?.projects;
        this.customerSharedProjects = productData?.sharedProjects;
      });
      this.customerLoading = false;
    },
    async lookupCustomer() {
      this.customerLoading = true;
      const fn = firebase.functions().httpsCallable("getUserAuthRecordByEmail");
      const resp = await fn({ userEmail: this.customerEmail });
      const user = resp.data;
      if (user.non_existent_user) {
        this.customerLoading = false;
        console.error("Not a Samply user.");
      } else {
        this.customer = user;
        this.fetchCustomerData();
      }
    }
  }
});
</script>

<style></style>
